import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import {
  Sparkles,
  Send,
  Code2,
  Eye,
  Download,
  ChevronLeft,
  Loader2,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  RefreshCw,
  Smartphone,
  Monitor,
  Tablet,
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useProjectStore } from '../store/projectStore'

type ViewMode = 'preview' | 'code' | 'split'
type DeviceMode = 'desktop' | 'tablet' | 'mobile'

export default function Editor() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const {
    currentProject,
    setCurrentProject,
    updateProject,
    generateWebsite,
    generating,
    generationStatus,
    fetchProjects,
    checkPendingGenerations,
  } = useProjectStore()

  const [prompt, setPrompt] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('preview')
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop')
  const [copied, setCopied] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [htmlContent, setHtmlContent] = useState('')
  const [cssContent, setCssContent] = useState('')
  const [activeTab, setActiveTab] = useState<'html' | 'css'>('html')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const { projects } = useProjectStore()

  useEffect(() => {
    if (!user) {
      navigate('/auth')
      return
    }

    fetchProjects(user.id)
  }, [user, navigate, fetchProjects])

  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find((p) => p.id === projectId)
      if (project) {
        setCurrentProject(project)
        setHtmlContent(project.html_content || '')
        setCssContent(project.css_content || '')
        // Check if there's an ongoing generation for this project
        checkPendingGenerations(projectId)
      }
    }
  }, [projectId, projects, setCurrentProject, checkPendingGenerations])

  useEffect(() => {
    if (currentProject) {
      setHtmlContent(currentProject.html_content || '')
      setCssContent(currentProject.css_content || '')
    }
  }, [currentProject])

  const handleGenerate = async () => {
    if (!prompt.trim() || !user || !currentProject) return

    await generateWebsite(currentProject.id, user.id, prompt)
    setPrompt('')

    // Refresh project data
    const { projects } = useProjectStore.getState()
    const updatedProject = projects.find((p) => p.id === currentProject.id)
    if (updatedProject) {
      setCurrentProject(updatedProject)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleGenerate()
    }
  }

  const handleCodeChange = async (type: 'html' | 'css', value: string) => {
    if (type === 'html') {
      setHtmlContent(value)
      if (currentProject) {
        await updateProject(currentProject.id, { html_content: value })
      }
    } else {
      setCssContent(value)
      if (currentProject) {
        await updateProject(currentProject.id, { css_content: value })
      }
    }
  }

  const getPreviewContent = () => {
    if (!htmlContent) return ''

    // Inject CSS into HTML
    const cssTag = `<style>${cssContent}</style>`
    if (htmlContent.includes('</head>')) {
      return htmlContent.replace('</head>', `${cssTag}</head>`)
    }
    return `${cssTag}${htmlContent}`
  }

  const handleCopyCode = () => {
    const code = activeTab === 'html' ? htmlContent : cssContent
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleExport = () => {
    const fullHtml = getPreviewContent()
    const blob = new Blob([fullHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${currentProject?.name || 'website'}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  const refreshPreview = () => {
    if (iframeRef.current) {
      iframeRef.current.src = 'about:blank'
      setTimeout(() => {
        if (iframeRef.current) {
          const doc = iframeRef.current.contentDocument
          if (doc) {
            doc.open()
            doc.write(getPreviewContent())
            doc.close()
          }
        }
      }, 50)
    }
  }

  const getDeviceWidth = () => {
    switch (deviceMode) {
      case 'mobile':
        return '375px'
      case 'tablet':
        return '768px'
      default:
        return '100%'
    }
  }

  if (!currentProject) {
    return (
      <div className="min-h-screen bg-midnight-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-ember-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-midnight-400">Loading project...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-screen bg-midnight-950 flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      <header className="flex-shrink-0 px-4 py-3 bg-midnight-900/80 backdrop-blur-xl border-b border-midnight-800 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-midnight-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <div className="h-6 w-px bg-midnight-700" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-midnight-950" />
            </div>
            <span className="font-display font-semibold text-white truncate max-w-[200px]">
              {currentProject.name}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="hidden md:flex items-center bg-midnight-800 rounded-lg p-1">
            {[
              { mode: 'preview' as const, icon: Eye, label: 'Preview' },
              { mode: 'code' as const, icon: Code2, label: 'Code' },
              { mode: 'split' as const, icon: Maximize2, label: 'Split' },
            ].map(({ mode, icon: Icon, label }) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  viewMode === mode
                    ? 'bg-midnight-700 text-white'
                    : 'text-midnight-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden lg:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Device Mode */}
          {(viewMode === 'preview' || viewMode === 'split') && (
            <div className="hidden lg:flex items-center bg-midnight-800 rounded-lg p-1">
              {[
                { mode: 'desktop' as const, icon: Monitor },
                { mode: 'tablet' as const, icon: Tablet },
                { mode: 'mobile' as const, icon: Smartphone },
              ].map(({ mode, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => setDeviceMode(mode)}
                  className={`p-1.5 rounded-md transition-colors ${
                    deviceMode === mode
                      ? 'bg-midnight-700 text-white'
                      : 'text-midnight-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          )}

          <button
            onClick={refreshPreview}
            className="p-2 text-midnight-400 hover:text-white hover:bg-midnight-800 rounded-lg transition-colors"
            title="Refresh Preview"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-3 py-2 text-midnight-300 hover:text-white hover:bg-midnight-800 rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-midnight-400 hover:text-white hover:bg-midnight-800 rounded-lg transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Preview Panel */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className={`${viewMode === 'split' ? 'w-1/2' : 'flex-1'} flex flex-col bg-midnight-900/50`}>
            <div className="flex-1 p-4 overflow-auto">
              <div
                className={`h-full mx-auto bg-midnight-950 rounded-lg shadow-2xl overflow-hidden transition-all duration-300 relative`}
                style={{ width: getDeviceWidth(), maxWidth: '100%', backgroundColor: '#0d0d10' }}
              >
                {generating && (
                  <div className="absolute inset-0 z-10 bg-midnight-950/90 backdrop-blur-sm flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ember-400 to-ember-600 flex items-center justify-center mx-auto mb-3 animate-pulse">
                        <Sparkles className="w-6 h-6 text-midnight-950" />
                      </div>
                      <h3 className="font-display text-lg font-semibold text-white mb-1">
                        Generating...
                      </h3>
                      <p className="text-midnight-400 text-sm">
                        {generationStatus || 'GPT-5.2 is working'}
                      </p>
                      <p className="text-midnight-500 text-xs mt-1">
                        1-2 min for complex designs
                      </p>
                    </div>
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  srcDoc={getPreviewContent()}
                  className="w-full h-full border-0 bg-midnight-950"
                  style={{ backgroundColor: '#0d0d10' }}
                  title="Preview"
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          </div>
        )}

        {/* Code Panel */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className={`${viewMode === 'split' ? 'w-1/2 border-l border-midnight-800' : 'flex-1'} flex flex-col`}>
            {/* Code Tabs */}
            <div className="flex items-center justify-between px-4 py-2 bg-midnight-900 border-b border-midnight-800">
              <div className="flex gap-1">
                {[
                  { tab: 'html' as const, label: 'HTML' },
                  { tab: 'css' as const, label: 'CSS' },
                ].map(({ tab, label }) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${
                      activeTab === tab
                        ? 'bg-midnight-800 text-white'
                        : 'text-midnight-400 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-midnight-400 hover:text-white hover:bg-midnight-800 rounded-lg transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-aurora-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            {/* Code Editor */}
            <div className="flex-1 overflow-auto">
              <textarea
                value={activeTab === 'html' ? htmlContent : cssContent}
                onChange={(e) => handleCodeChange(activeTab, e.target.value)}
                className="w-full h-full p-4 bg-midnight-950 text-midnight-100 font-mono text-sm resize-none focus:outline-none code-editor"
                spellCheck={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Prompt Input */}
      <div className="flex-shrink-0 p-4 bg-midnight-900/80 backdrop-blur-xl border-t border-midnight-800">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you want to build... (e.g., 'Create a modern landing page for a SaaS product with pricing section')"
              rows={2}
              className="w-full px-4 py-3 pr-14 bg-midnight-800 border border-midnight-700 rounded-xl text-white placeholder-midnight-500 focus:outline-none focus:border-ember-500 transition-colors resize-none"
              disabled={generating}
            />
            <button
              onClick={handleGenerate}
              disabled={generating || !prompt.trim()}
              className="absolute right-3 bottom-3 p-2 bg-gradient-to-r from-ember-400 to-ember-500 text-midnight-950 rounded-lg hover:shadow-lg hover:shadow-ember-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-midnight-500 text-center">
            Press Enter to generate • Shift+Enter for new line
          </p>
        </div>
      </div>

    </div>
  )
}

